import { ApolloProvider } from '@apollo/client';
import {
    AppInsightsContext,
    AppInsightsErrorBoundary,
} from '@microsoft/applicationinsights-react-js';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { createRoot } from 'react-dom/client';
import {
    type RouteObject,
    RouterProvider,
    createBrowserRouter,
} from 'react-router-dom';

import { setupTemporalPolyfill } from '@myphx-engineering/date-utils/client';
import { TalkativeInitializer } from '@phx/talkative';

import { SsoApiClient } from './api/sso-api-client';
import { Layout } from './components/layout/Layout';
import { BrandProvider, useBrandContext } from './contexts/BrandProvider';
import { createApplicationInsights } from './instrumentation';
import AccountCreationError from './pages/account-creation-error/AccountCreationErrorPage';
import ChangePassword from './pages/change-password/ChangePasswordPage';
import InitiateProfileUpdate from './pages/initiate-profile-update/InitiateProfileUpdatePage';
import InitiateTokenCallback from './pages/initiate-token-callback/InitiateTokenCallbackPage';
import MfaSelectPage from './pages/mfa/MfaSelectPage';
import TotpPreSetupPage from './pages/mfa/TotpPreSetupPage';
import { TotpSetupPage } from './pages/mfa/TotpSetupPage';
import InvalidLink from './pages/not-found/InvalidLink';
import NotFoundPage from './pages/not-found/NotFoundPage';
import { LillySignUp } from './pages/partner/lilly/sign-up/LillySignUpPage';
import RecoverPasswordLink from './pages/recover-password-link/RecoverPasswordLinkPage';
import RecoverPassword from './pages/recover-password/RecoverPasswordPage';
import RecoverPasswordSendCode from './pages/recover-password/RecoverPasswordSendCodePage';
import RecoverPasswordVerifyCode from './pages/recover-password/RecoverPasswordVerifyCodePage';
import Signin from './pages/sign-in/SignInPage';
import SignupChallengeQuestions from './pages/sign-up-challenge-questions/SignUpChallengeQuestionsPage';
import Signup from './pages/sign-up/SignUpPage';
import SsoRedirect from './pages/sso-redirect/SsoRedirectPage';
import TotpVerifyPage from './pages/totp-verify/TotpVerifyPage';
import VerifyAccount from './pages/verify-account/VerifyAccountPage';
import { routes } from './routes';
import '@myphx-engineering/phx-theme/theme.css';
import './i18n';
import './index.css';

setupTemporalPolyfill();

const signUpByPartner: Record<string, React.FC> = {
    LillyDirect: LillySignUp,
    Prescryptive: Signup,
};
const BrandedSignUp = () => {
    const { brandId } = useBrandContext();
    const SignUpComponent = signUpByPartner[brandId] || Signup;
    return <SignUpComponent />;
};

const allowedRoutes: RouteObject[] = [
    {
        element: <Layout />,
        children: [
            {
                path: routes.signIn,
                element: <Signin />,
            },
            {
                path: routes.signUp,
                element: <BrandedSignUp />,
            },
            {
                path: routes.signUpError,
                element: <AccountCreationError />,
            },
            {
                path: routes.signUpChallengeQuestions,
                element: <SignupChallengeQuestions />,
            },
            {
                path: routes.verify,
                element: <VerifyAccount />,
            },
            {
                path: routes.mfaSelect,
                element: <MfaSelectPage />,
            },
            {
                path: routes.totpSetup,
                element: <TotpSetupPage />,
            },
            {
                path: routes.totpPreSetup,
                element: <TotpPreSetupPage />,
            },
            {
                path: routes.totpVerify,
                element: <TotpVerifyPage />,
            },
            {
                path: routes.sso,
                element: <SsoRedirect />,
            },
            {
                path: routes.recoverSendCode,
                element: <RecoverPasswordSendCode />,
            },
            {
                path: routes.recoverVerifyCode,
                element: <RecoverPasswordVerifyCode />,
            },
            {
                path: routes.recover,
                element: <RecoverPassword />,
            },
            {
                path: routes.recoverLink,
                element: <RecoverPasswordLink />,
            },
            {
                path: routes.profile,
                element: <ChangePassword />,
            },
            {
                path: routes.update,
                element: <InitiateProfileUpdate />,
            },
            {
                path: routes.tokenCallback,
                element: <InitiateTokenCallback />,
            },
            {
                path: routes.root,
                element: <NotFoundPage />,
            },
            {
                path: routes.invalidLink,
                element: <InvalidLink />,
            },
            {
                path: routes.wildcard,
                element: <NotFoundPage />,
            },
        ],
    },
];

const router = createBrowserRouter(allowedRoutes, {
    basename: '/',
});
const ldClientSideID = import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID;

const { reactPlugin } = createApplicationInsights();

asyncWithLDProvider({
    user: {
        anonymous: true,
    },
    clientSideID: ldClientSideID,
}).then((LDProvider) => {
    createRoot(document.getElementById('root') as HTMLElement).render(
        <React.StrictMode>
            <AppInsightsContext.Provider value={reactPlugin}>
                <LDProvider>
                    <BrandProvider>
                        <AppInsightsErrorBoundary
                            onError={() => (
                                <h3>
                                    An unexpected error occurred during sign in.
                                    Please try again.
                                </h3>
                            )}
                            appInsights={reactPlugin}
                        >
                            <ApolloProvider client={SsoApiClient}>
                                <TalkativeInitializer />
                                <RouterProvider router={router} />
                            </ApolloProvider>
                        </AppInsightsErrorBoundary>
                    </BrandProvider>
                </LDProvider>
            </AppInsightsContext.Provider>
        </React.StrictMode>
    );
});
